/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useMemo } from 'react';
import { Checkbox, Link, Typography } from '@one-thd/sui-atomic-components';
import proptypes from 'prop-types';
import classNames from 'classnames';
import { GroupAccordionContext } from '../GroupAccordionContext';

const { func, bool, string } = proptypes;

const ActionBar = ({
  renderActionButtons,
  onExpandAll,
  onCollapseAll,
  mobileActionBar,
  onSelectAllGroupsAndItems,
  errorSelectionMessage,
  hideSelectAll
}) => {
  const {
    selectedItems,
    onUnselectAll,
    setExpandedItems,
    expandedItems,
    data,
    disableSelection,
    onSelectAll,
  } = useContext(GroupAccordionContext);

  const count = useMemo(() => {
    // Counts the total number of items and groups
    return data.reduce((acc, group) => ({
      itemCount: acc.itemCount + group.noOfItems,
      groupCount: !group.isDefault ? acc.groupCount + 1 : acc.groupCount
    }), {
      itemCount: 0,
      groupCount: 0
    });
  }, [data]);

  const selectedItemsCount = useMemo(() => {
    return Object.keys(selectedItems).reduce((acc, groupId) => {
      return {
        // Count the selected items in the group
        itemCount: acc.itemCount + selectedItems[groupId].items.length,
        // Count the group if the group is selected
        groupCount: acc.groupCount + (selectedItems[groupId].isSelected ? 1 : 0)
      };
    }, {
      itemCount: 0,
      groupCount: 0
    });
  }, [selectedItems]);

  // Check if all items and groups are selected
  const isAllSelected = count.itemCount === selectedItemsCount.itemCount
    && count.groupCount === selectedItemsCount.groupCount;

  const handleSelectAll = () => {
    onSelectAll();
    onSelectAllGroupsAndItems();
  };

  const handleUnselectAll = () => {
    onUnselectAll();
  };

  const allExpanded = React.useMemo(() => {
    // TODO: How should we handle infinite scrolling?
    return expandedItems.length === data.length;
  }, [expandedItems]);

  const handleCollapseAll = (event) => {
    setExpandedItems([]);
    onCollapseAll(event);
  };
  const handleExpandAll = (event) => {
    const groupIds = (data || []).map((group) => group.id);
    setExpandedItems(groupIds);
    onExpandAll(event);
  };

  const groupCountStyles = classNames(
    'sui-w-full sui-flex sui-gap-2',
    {
      'sui-mb-5': !errorSelectionMessage
    }
  );

  const SelectAllUI = () => (
    <div className="sui-flex sui-flex-row sui-gap-6">
      {!disableSelection && (
        <Checkbox
          checked={isAllSelected}
          onChange={isAllSelected ? handleUnselectAll : handleSelectAll}
        />
      )}
      {!disableSelection && !hideSelectAll && (
        <Link
          component="button"
          onClick={isAllSelected ? handleUnselectAll : handleSelectAll}
        >
          {isAllSelected ? 'Unselect All' : 'Select All'}
        </Link>
      )}
      <Link
        component="button"
        onClick={allExpanded ? handleCollapseAll : handleExpandAll}
      >
        {allExpanded ? 'Collapse All' : 'Expand All'}
      </Link>
    </div>
  );

  const groupText = selectedItemsCount.groupCount === 1 ? 'group' : 'groups';
  const itemText = selectedItemsCount.itemCount === 1 ? 'item' : 'items';

  const GroupItemCounterUI = () => (
    <div className={groupCountStyles}>
      <Typography weight="bold">{selectedItemsCount.groupCount} {groupText} selected</Typography>
      <Typography>{selectedItemsCount.itemCount} {itemText} selected</Typography>
    </div>
  );

  return (
    <div
      data-testid="action-bar-wrapper"
    >
      { mobileActionBar && (
        <div className="sui-w-full sui-flex sui-flex-col sui-gap-2 sui-mb-5">
          <SelectAllUI />
          <GroupItemCounterUI />
          {renderActionButtons({ selectedItems })}
        </div>
      )}
      { !mobileActionBar && (
        <>
          <div className="sui-w-full sui-flex sui-flex-row sui-justify-between sui-items-center">
            <SelectAllUI />
            {renderActionButtons({ selectedItems })}
          </div>
          <GroupItemCounterUI />
        </>
      )}
      { errorSelectionMessage && (
        <div className="sui-w-full sui-mb-5">
          <Typography color="danger">{errorSelectionMessage}</Typography>
        </div>
      ) }
    </div>
  );
};

ActionBar.propTypes = {
  onExpandAll: func,
  onCollapseAll: func,
  renderActionButtons: func,
  mobileActionBar: bool,
  onSelectAllGroupsAndItems: func,
  errorSelectionMessage: string,
  hideSelectAll: bool,
};

ActionBar.defaultProps = {
  renderActionButtons: () => {},
  onExpandAll: () => {},
  onCollapseAll: () => {},
  mobileActionBar: false,
  onSelectAllGroupsAndItems: () => {},
  errorSelectionMessage: null,
  hideSelectAll: false,
};

ActionBar.displayName = 'ActionBar';

export { ActionBar };
